import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { platformToast } from "$root/notification-system/toast";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { multiRef } from "$root/utils";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { type BulkActionItem, BulkActions } from "@mdotm/shadcn/mdotm/components";
import type { ColDef, GetRowIdFunc, RowClassRules } from "ag-grid-community";
import type { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { type Ref, useMemo, useRef } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { InstrumentEditorBuilderProps } from "../builder";
import type { Instrument } from "../const";
import type { InstrumentCompositionEditorProps } from "../instrumentEditor.types";
import { withNewInstrumentRowClasses, withUnfixableUnselectable } from "../quantity/new-instruments/rowStyling";
import { pinnedIssueColumnGridProps } from "../quantity/new-instruments/pinnedIssueColumn";
import { withHeldBlockedRowClasses, withHeldBlockedUnselectable } from "../quantity/held-issues/rowStyling";
import { cashRowSelectionGuard, pinnedCashRowGridProps } from "../quantity/pinnedCashRow";

export function InstrumentEditorTable(
	props: InstrumentCompositionEditorProps & {
		rows: Instrument[];
		columns: ColDef<Instrument>[];
		agGridRef?: Ref<AgGridReact<Instrument>>;
		selection: Instrument[];
		onChangeSelection(selection: Instrument[]): void;
	} & Partial<AgGridReactProps<Instrument>>,
): React.JSX.Element {
	const persistColProps = agExtra.usePersistColState(
		props.compositionMode === "quantity"
			? props.entity === "INVESTMENT_ENHANCEMENT"
				? persistedColStateKeys["tableV5-investment-quantityProposalComposition-edit"]
				: persistedColStateKeys["tableV4-investment-quantityComposition-edit"]
			: {
					BENCHMARK: persistedColStateKeys["tableV3-investment-composition-edit"],
					TARGET_INVESTMENT: persistedColStateKeys["tableV3-investment-composition-edit"],
					INVESTMENT: persistedColStateKeys["tableV3-investment-composition-edit"],
					INVESTMENT_DRAFT: persistedColStateKeys["tableV3-investment-composition-edit"],
					INVESTMENT_ENHANCEMENT: persistedColStateKeys["tableV3-investment-proposalComposition-edit"],
					UNIVERSE: persistedColStateKeys["tableV3-universe-composition-edit"],
				}[props.entity],
	);
	//ts pattern is the best opt
	if (
		props.entity === "INVESTMENT" ||
		props.entity === "TARGET_INVESTMENT" ||
		props.entity === "BENCHMARK" ||
		props.entity === "INVESTMENT_DRAFT" ||
		props.entity === "INVESTMENT_ENHANCEMENT"
	) {
		return props.mode === "new" ? (
			<InstrumentEditorTableInner {...props} deleteMode="hard" {...persistColProps} />
		) : (
			<InstrumentEditorTableInner
				{...props}
				deleteMode="soft"
				bulkActions={
					props.entity === "INVESTMENT_ENHANCEMENT" ? ["delete", "restore"] : ["delete", "restore", "compare"]
				}
				{...persistColProps}
			/>
		);
	}

	if (props.entity === "UNIVERSE") {
		return props.mode === "new" ? (
			<InstrumentEditorTableInner {...props} deleteMode="hard" bulkActions={["delete"]} {...persistColProps} />
		) : (
			<InstrumentEditorTableInner
				{...props}
				deleteMode="soft"
				bulkActions={["delete", "restore"]}
				{...persistColProps}
			/>
		);
	}

	return <div>Criteria not matching, please use sphere navigation</div>;
}

const EDITOR_ROW_CLASS_RULES: RowClassRules<Instrument> = {
	"bg-[var(--global-palette-warning-100)]": (params) => {
		return Boolean(params.data?.someInstrumentsAreExcluded);
	},
	"bg-[var(--Table-highlightedRowBackgroundColor)]": (params) => {
		return Boolean(params.data?.deleted);
	},
};

function InstrumentEditorTableInner({
	rows,
	columns,
	// Kept out of `forward`, which goes to the grid as grid options.
	limit: _limit,
	instrumentBuilder,
	bulkActions = ["delete", "compare"],
	deleteMode = "hard",
	onChangeSelection,
	selection,
	agGridRef,
	compositionMode,
	...forward
}: {
	rows: Instrument[];
	columns: ColDef<Instrument>[];
	compositionMode?: "quantity" | "weight";
	instrumentBuilder: InstrumentEditorBuilderProps;
	minThreshold?: number;
	limit?: number;
	selection: Array<Instrument>;
	onChangeSelection(newSelection: Array<Instrument>): void;
	agGridRef?: Ref<AgGridReact<Instrument>>;
} & Partial<AgGridReactProps> &
	(
		| {
				deleteMode?: "soft";
				bulkActions?: Array<InstrumentBuilderActions>;
		  }
		| {
				deleteMode?: "hard";
				bulkActions?: Array<Extract<InstrumentBuilderActions, "delete" | "compare">>;
		  }
	)) {
	const innerAgGridRef = useRef<AgGridReact<Instrument>>(null);
	const availableActions = instrumentBuilderActions(instrumentBuilder, innerAgGridRef.current!);
	const actions = useMemo(
		() =>
			bulkActions?.map((action) => {
				if (action === "delete") {
					return availableActions.delete(deleteMode);
				}

				return availableActions[action]();
			}),
		[bulkActions, availableActions, deleteMode],
	);

	return (
		<>
			<BulkActions
				variant="floating"
				onClear={() => innerAgGridRef.current?.api.deselectAll()}
				count={selection.length}
				actions={actions}
				className="my-2"
			/>
			<AgGridWithModules<Instrument>
				innerRef={multiRef(agGridRef, innerAgGridRef)}
				{...agExtra.aggFunctions()}
				{...agExtra.checkboxColumn({
					mode: "multiRow",
					selectAll: "currentPage",
					checkboxLocation: "autoGroupColumn",
					groupSelects: "descendants",
					...(compositionMode === "quantity"
						? withHeldBlockedUnselectable(withUnfixableUnselectable(cashRowSelectionGuard))
						: {}),
				})}
				{...forward}
				{...(compositionMode === "quantity" ? pinnedCashRowGridProps<Instrument>() : {})}
				{...(compositionMode === "quantity" ? pinnedIssueColumnGridProps : {})}
				columnDefs={columns}
				rowData={rows}
				grandTotalRow="pinnedBottom"
				onSelectionChanged={(params) => onChangeSelection(params.api.getSelectedRows())}
				getRowId={getRowId}
				onCellValueChanged={(params) => {
					if (params.column.getColId() === "ACTION") {
						if (params.node.group) {
							const rowsId = params.node.allLeafChildren?.map((child) => child.data!.id) ?? [];
							toggleDelete(deleteMode, instrumentBuilder, { ids: rowsId, newValue: params.newValue });
							return;
						}

						toggleDelete(deleteMode, instrumentBuilder, { ids: params.data.id, newValue: params.newValue });
					}

					// In quantity mode WEIGHT commits inside its valueSetter (withQuantityCommitHandlers):
					// the newValue here is re-read through the valueGetter after the setter and is stale.
					if (params.column.getColId() === "WEIGHT" && compositionMode !== "quantity") {
						instrumentBuilder.setWeight(params.data.id, params.newValue);

						if (params.data.instrumentType === "INVESTMENT") {
							const weight = params.newValue ?? 0;
							instrumentBuilder.setInstrument(params.data.id, { ...params.data, weight, isValidating: true });
							instrumentsRepository.verifyInvestmentComposition
								.fetchQuery(params.data.investmentUuid, weight, "INVESTMENT")
								.then(({ removedTickers }) => {
									const someInstrumentAreRemoved = removedTickers ?? [];
									instrumentBuilder.setInstrument(params.data.id, {
										...params.data,
										nOfInstrumentExcluded: someInstrumentAreRemoved.length,
										someInstrumentsAreExcluded: someInstrumentAreRemoved.length > 0,
										isValidating: false,
										weight,
									});
								})
								.catch(() => {
									platformToast({
										icon: "Icon-full-error",
										children: "Something went wrong, unable to validate instrument",
										severity: "error",
									});

									instrumentBuilder.setInstrument(params.data.id, { ...params.data, weight, isValidating: false });
								});
						}
					}
				}}
				rowClassRules={
					compositionMode === "quantity"
						? withHeldBlockedRowClasses(withNewInstrumentRowClasses(EDITOR_ROW_CLASS_RULES))
						: EDITOR_ROW_CLASS_RULES
				}
				getContextMenuItems={(params) =>
					params.node?.data?.instrumentType === "INVESTMENT"
						? [
								"copy",
								"paste",
								{
									name: "Compare",
									disabled: params.node?.data?.instrumentType !== "INVESTMENT",
									action(actionParams) {
										if (!actionParams.node?.data || actionParams.node?.data?.instrumentType !== "INVESTMENT") {
											throw new Error("Expected investment");
										}
										instrumentBuilder.setInstrumentToCompare(
											Array.from(
												instrumentBuilder
													.latestComparedInstruments()
													.set(actionParams.node.data.id, actionParams.node.data)
													.valueSeq()
													.toArray(),
											),
										);
									},
									icon: renderToStaticMarkup(<Icon icon="compare" size={20} />),
								},
							]
						: ["copy", "paste"]
				}
				stopEditingWhenCellsLoseFocus
				undoRedoCellEditing
				undoRedoCellEditingLimit={20}
			/>
		</>
	);
}

const getRowId: GetRowIdFunc<Instrument, any> = (row) => {
	return row.data.id;
};

function toggleDelete(
	deleteMode: string,
	instrumentBuilder: InstrumentEditorBuilderProps,
	params: { ids: string | Iterable<string>; newValue: boolean },
) {
	if (deleteMode === "hard") {
		instrumentBuilder.delete("hard", params.ids);
	} else {
		instrumentBuilder.delete("soft", params.ids, params.newValue);
	}
}

function instrumentBuilderActions(instrumentBuilder: InstrumentEditorBuilderProps, agGrid: AgGridReact<Instrument>) {
	return {
		delete: (mode: "hard" | "soft") => ({
			label: "Delete",
			icon: "Delete",
			onClick: () => {
				const selection = agGrid.api.getSelectedRows();
				const selectionIds = selection.map((row) => row.id);
				if (selectionIds.every((id) => instrumentBuilder.isInstrumentDeleted(id))) {
					platformToast({
						children: "This instrument was already deleted. Please select another",
						icon: "info",
						severity: "info",
					});
					return;
				}

				if (mode === "hard") {
					instrumentBuilder.delete("hard", selectionIds);
				} else {
					instrumentBuilder.delete("soft", selectionIds, true);
					const rowsToUpdate = selection.map((row) => ({ ...row, deleted: true }));
					agGrid.api.applyTransactionAsync({ update: rowsToUpdate });
				}

				agGrid.api.deselectAll();
			},
		}),
		restore: () => ({
			label: "Restore",
			icon: "restore",
			onClick: () => {
				const selection = agGrid.api.getSelectedRows();
				const selectionIds = selection.map((row) => row.id);

				const someInstrumentsAreDeleted = selectionIds.some((id) => instrumentBuilder.isInstrumentDeleted(id));
				if (!someInstrumentsAreDeleted) {
					platformToast({
						children: "Restore is available only for deleted instruments",
						icon: "info",
						severity: "info",
					});
					return;
				}
				instrumentBuilder.delete("soft", selectionIds, false);
				const rowsToUpdate = selection.map((row) => ({ ...row, deleted: false }));
				agGrid.api.applyTransactionAsync({ update: rowsToUpdate });
				agGrid.api.deselectAll();
			},
		}),
		compare: () => ({
			label: "Compare",
			icon: "compare",
			onClick: () => {
				const selection = agGrid.api.getSelectedRows().map((row) => row.id);
				const investments = selection.flatMap((id) => {
					const instrument = instrumentBuilder.getInstrument(id);
					return instrument && instrument.instrumentType === "INVESTMENT" ? [instrument] : [];
				});

				if (investments.length === 0) {
					platformToast({
						children: "Compare is available only for portfolios within the composition",
						icon: "info",
						severity: "info",
					});
					return;
				}

				instrumentBuilder.setInstrumentToCompare(investments);
				agGrid.api.deselectAll();
			},
		}),
	} satisfies Record<string, (...params: any[]) => BulkActionItem>;
}

type InstrumentBuilderActions = keyof ReturnType<typeof instrumentBuilderActions>;
